/**
 * SHA-256, in pure TypeScript.
 *
 * ## Why not `node:crypto` or `SubtleCrypto`
 *
 * This runs in four places — the API, a browser, a Worker, and Hermes on a
 * phone — and none of the platform hashes are available in all four. Node's
 * `crypto` does not exist in a browser; `crypto.subtle` is asynchronous, is
 * absent on insecure origins, and is one of the globals the script sandbox
 * DELETES before user code runs. A synchronous, dependency-free function is
 * available everywhere and gives the same answer in every one of them, which
 * is the whole point of a fingerprint.
 *
 * ## Why a real hash rather than a cheap one
 *
 * The fingerprint identifies a script's source, and scripts are SHARED. The
 * adversary is therefore the author of a script, who has an obvious motive to
 * make their code carry the fingerprint of code somebody already trusted. A
 * 32- or 64-bit non-cryptographic hash can be made to collide in seconds.
 * SHA-256 costs about a millisecond on a 64KB script, once, at install.
 *
 * Verified in `script-hash.test.ts` against the published NIST vectors and
 * against multi-byte and multi-block inputs.
 */

/* The first 32 bits of the fractional parts of the cube roots of the first 64
   primes. Straight from FIPS 180-4 §4.2.2; not derived at runtime, because a
   table that computes itself is a table nobody can check by eye. */
const K = [
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1,
  0x923f82a4, 0xab1c5ed5, 0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3,
  0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174, 0xe49b69c1, 0xefbe4786,
  0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
  0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147,
  0x06ca6351, 0x14292967, 0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13,
  0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85, 0xa2bfe8a1, 0xa81a664b,
  0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
  0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a,
  0x5b9cca4f, 0x682e6ff3, 0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208,
  0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
];

/**
 * UTF-8 bytes of a string, without `TextEncoder`.
 *
 * Hand-rolled because `TextEncoder` is not reliably present in every
 * JavaScript engine this package is loaded into, and a fingerprint that is
 * computed on one platform and compared on another has to agree byte for byte.
 *
 * Lone surrogates — which a string can legally hold and which JSON round-trips
 * — are encoded as U+FFFD, matching what `TextEncoder` does. Silently dropping
 * them would let two different sources share a fingerprint.
 */
export function utf8Bytes(text: string): number[] {
  const out: number[] = [];
  for (let i = 0; i < text.length; i += 1) {
    let code = text.charCodeAt(i);
    if (code >= 0xd800 && code <= 0xdbff) {
      const next = text.charCodeAt(i + 1);
      if (next >= 0xdc00 && next <= 0xdfff) {
        code = 0x10000 + ((code - 0xd800) << 10) + (next - 0xdc00);
        i += 1;
      } else {
        code = 0xfffd;
      }
    } else if (code >= 0xdc00 && code <= 0xdfff) {
      code = 0xfffd;
    }

    if (code < 0x80) {
      out.push(code);
    } else if (code < 0x800) {
      out.push(0xc0 | (code >> 6), 0x80 | (code & 0x3f));
    } else if (code < 0x10000) {
      out.push(0xe0 | (code >> 12), 0x80 | ((code >> 6) & 0x3f), 0x80 | (code & 0x3f));
    } else {
      out.push(
        0xf0 | (code >> 18), 0x80 | ((code >> 12) & 0x3f),
        0x80 | ((code >> 6) & 0x3f), 0x80 | (code & 0x3f),
      );
    }
  }
  return out;
}

function rotr(x: number, n: number): number {
  return (x >>> n) | (x << (32 - n));
}

/** Lowercase hex SHA-256 of a string's UTF-8 bytes. */
export function sha256Hex(text: string): string {
  const bytes = utf8Bytes(text);
  const bitLen = bytes.length * 8;

  /* Padding: one 0x80 byte, then zeros until the length is 56 mod 64, then the
     bit length as a big-endian 64-bit integer. */
  bytes.push(0x80);
  while (bytes.length % 64 !== 56) bytes.push(0);
  /* The high word. Written from the byte count rather than from `bitLen` so
     inputs above 512MB do not overflow a 32-bit shift into nonsense. */
  const high = Math.floor(bitLen / 0x100000000);
  bytes.push(
    (high >>> 24) & 0xff, (high >>> 16) & 0xff, (high >>> 8) & 0xff, high & 0xff,
    (bitLen >>> 24) & 0xff, (bitLen >>> 16) & 0xff, (bitLen >>> 8) & 0xff, bitLen & 0xff,
  );

  /* The first 32 bits of the fractional parts of the square roots of the first
     eight primes. FIPS 180-4 §5.3.3. */
  let h0 = 0x6a09e667; let h1 = 0xbb67ae85; let h2 = 0x3c6ef372; let h3 = 0xa54ff53a;
  let h4 = 0x510e527f; let h5 = 0x9b05688c; let h6 = 0x1f83d9ab; let h7 = 0x5be0cd19;

  const w = new Array<number>(64);

  for (let chunk = 0; chunk < bytes.length; chunk += 64) {
    for (let i = 0; i < 16; i += 1) {
      const p = chunk + i * 4;
      w[i] = ((bytes[p]! << 24) | (bytes[p + 1]! << 16)
        | (bytes[p + 2]! << 8) | bytes[p + 3]!) >>> 0;
    }
    for (let i = 16; i < 64; i += 1) {
      const a = w[i - 15]!;
      const b = w[i - 2]!;
      const s0 = rotr(a, 7) ^ rotr(a, 18) ^ (a >>> 3);
      const s1 = rotr(b, 17) ^ rotr(b, 19) ^ (b >>> 10);
      w[i] = (w[i - 16]! + s0 + w[i - 7]! + s1) >>> 0;
    }

    let a = h0; let b = h1; let c = h2; let d = h3;
    let e = h4; let f = h5; let g = h6; let h = h7;

    for (let i = 0; i < 64; i += 1) {
      const s1 = rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25);
      const ch = (e & f) ^ (~e & g);
      const t1 = (h + s1 + ch + K[i]! + w[i]!) >>> 0;
      const s0 = rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22);
      const maj = (a & b) ^ (a & c) ^ (b & c);
      const t2 = (s0 + maj) >>> 0;

      h = g; g = f; f = e; e = (d + t1) >>> 0;
      d = c; c = b; b = a; a = (t1 + t2) >>> 0;
    }

    h0 = (h0 + a) >>> 0; h1 = (h1 + b) >>> 0; h2 = (h2 + c) >>> 0; h3 = (h3 + d) >>> 0;
    h4 = (h4 + e) >>> 0; h5 = (h5 + f) >>> 0; h6 = (h6 + g) >>> 0; h7 = (h7 + h) >>> 0;
  }

  return [h0, h1, h2, h3, h4, h5, h6, h7]
    .map((x) => x.toString(16).padStart(8, '0'))
    .join('');
}

/**
 * The fingerprint of a script's source.
 *
 * Line endings are normalised first, and only then hashed. A script that
 * round-trips through a Windows editor is the same script, and a "the source
 * changed since you installed it" warning that fires on invisible carriage
 * returns is a warning traders learn to click through.
 *
 * Nothing else is normalised. Whitespace inside the code is significant: in
 * JavaScript it can change what the code DOES, and a fingerprint that ignores
 * it would let an author ship different behaviour under an unchanged hash.
 */
export function scriptFingerprint(source: string): string {
  return sha256Hex(source.replace(/\r\n/g, '\n').replace(/\r/g, '\n'));
}
